import axios from "axios";
import { ApiException } from "@/services/api/ApiExceptions";
import {
  IProcess,
  IArchivedProcess,
  IProcessId,
  IProcessFavorite,
  IProcessResponsible,
  IDispatch,
  IProcessAppend,
  IReportRequest,
  IProcessMigrate,
  IProcessForward,
  IProcessPendency,
  IProcessGroupRequest
} from "@/store/types/process-type";
import { FilterPaged, FullFilterPaged } from "@/store/types/types";
import {
  IDocumentDelete,
  IDocumentJoin,
  IProcessReturn
} from "@/store/types/document-type";
import { IProcessFileComplete } from "@/store/types/file-type";
import { IGiveKnowledgeRequest } from "@/store/types/user-types";

// Process End Points
export const getProcessAutoDraft = () =>
  axios({
    url: "process/auto-draft",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProcessAutoDraft = (process: IProcess) =>
  axios({
    url: `process/auto-draft/${process.id}`,
    data: process,
    method: "PUT"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessDraftList = ({
  page,
  order_by,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: "process/draft",
    method: "GET",
    params: {
      page,
      order_by,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProcessDraft = (process: IProcess) =>
  axios({
    url: `process/draft/${process.id}`,
    data: process,
    method: "PUT"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteProcessDraft = (processIdList: IProcessId[]) =>
  axios({
    url: `process/draft/delete/`,
    method: "DELETE",
    data: processIdList
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteDispatchDraft = (dispatchId: string) =>
  axios({
    url: `process/dispatch/auto-draft/delete/`,
    method: "DELETE",
    data: { dispatch_id: dispatchId }
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const editProcessDraft = (processId: string) =>
  axios({
    url: `process/draft/edit/${processId}`,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcess = (processId: string) =>
  axios({
    url: `process/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessList = ({
  page,
  order_by,
  paginator_count = 10,
  search
}: FilterPaged) =>
  axios({
    url: `process/list`,
    method: "GET",
    params: { page, order_by, paginator_count, search }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessListByCapacity = ({
  capacityId,
  page,
  order_by,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  with_children = false,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: `process/list/${capacityId}`,
    method: "GET",
    params: {
      page,
      order_by,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      with_children,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createProcess = (process: IProcess) =>
  axios({
    url: `process/create/${process.id}`,
    data: process,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setModifiedProcess = ({
  data,
  processId
}: {
  data: IProcess;
  processId: string;
}) =>
  axios({
    url: `process/patch/${processId}`,
    data: data,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setModifiedBookmarkProcess = ({
  data,
  processId
}: {
  data: IProcess;
  processId: string;
}) =>
  axios({
    url: `process/bookmark/${processId}`,
    data: data,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const orderProcessFile = (processFiles: IProcessFileComplete[]) =>
  axios({
    url: "process/file/edit/sequence",
    method: "PATCH",
    params: { page: 1, paginator_count: 999 },
    data: processFiles
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteProcessFile = ({
  processFileId,
  isArchive,
  isRevertNoEffect
}: IDocumentDelete) =>
  axios({
    url: `/process/file/delete/${processFileId}`,
    method: "DELETE",
    data: { archive: isArchive, revert_no_effect: isRevertNoEffect }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessesCompleteFile = (processIdList: IProcessId[]) =>
  axios({
    url: `process/downloads`,
    method: "POST",
    data: processIdList
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSignatureDocuments = (processFileIdList: string[]) =>
  axios({
    url: `process/signature/downloads`,
    method: "POST",
    data: processFileIdList
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setKnowledgeFile = ({
  process_file_ids,
  process_id,
  user,
  LGPD,
  observation,
  is_external
}: IGiveKnowledgeRequest) =>
  axios({
    url: `process/giveknowledge`,
    method: "POST",
    data: { process_file_ids, process_id, user, LGPD, observation, is_external }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setKnowledgeProcess = ({
  process_id,
  user,
  LGPD,
  observation,
  is_external
}: IGiveKnowledgeRequest) =>
  axios({
    url: `process/giveknowledge/process/${process_id}`,
    method: "POST",
    data: { user, LGPD, observation, is_external }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessHistory = (processId: string) =>
  axios({
    url: `process/history/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPublicHistory = (processId: string) =>
  axios({
    url: `process/public-history/${processId}/`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getArchivesProcess = ({
  page,
  order_by,
  capacity,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  with_children = false,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: `process/archive/list`,
    params: {
      page,
      order_by,
      capacity,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      with_children,
      only_count
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setArchivesProcess = (process_list: IArchivedProcess) =>
  axios({
    url: `process/archive`,
    data: process_list,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getArchivesJustificationList = () =>
  axios({
    url: `process/archive/justification-list/`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getFavoriteProcess = ({
  page,
  order_by,
  capacity,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: `process/favorite/list`,
    params: {
      page,
      order_by,
      capacity,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setFavoriteProcess = (processIdList: IProcessFavorite[]) =>
  axios({
    url: `process/desfavorite`,
    method: "PATCH",
    data: processIdList
  })
    .then(() => {
      return { kind: "ok" };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessProcessed = ({
  page,
  order_by,
  capacity,
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  with_children = false,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: `process/tramited/list`,
    params: {
      page,
      order_by,
      capacity,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      with_children,
      only_count
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getMyProcessList = ({
  page,
  order_by = JSON.stringify({
    user_responsible_date: { order: "asc", unmapped_type: "date" }
  }),
  paginator_count = 10,
  search,
  filters,
  ignore_order_by_default,
  only_count = undefined
}: FilterPaged) =>
  axios({
    url: `process/my-process`,
    method: "GET",
    params: {
      page,
      order_by,
      paginator_count,
      search,
      ...filters,
      ignore_order_by_default,
      only_count
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setUserResponsible = (userResponsible: IProcessResponsible) =>
  axios({
    url: `process/responsible`,
    data: userResponsible,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const hasAutoDraftWrittenRequest = ({
  processId,
  capacityId
}: {
  processId: string;
  capacityId: number;
}) =>
  axios({
    url: `/process/dispatch/has-auto-draft-written/${processId}/`,
    method: "GET",
    params: {
      capacity_id: capacityId
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Dispatch End Points
export const setDispatchAutoDraft = (dispatch: IDispatch) =>
  axios({
    url: `process/dispatch/auto-draft/update/${dispatch.id}`,
    data: dispatch,
    method: "PUT"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createDispatch = (dispatch: IDispatch) =>
  axios({
    url: `process/dispatch/create/${dispatch.id}`,
    data: dispatch,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDispatchAutoDraft = (processId: string) =>
  axios({
    url: `process/dispatch/auto-draft/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setDispatchAutoProcessing = (
  dispatchId: string,
  automatic: boolean
) =>
  axios({
    url: `process/automatic-processing-dispatch/${dispatchId}`,
    data: { automatic },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProcessAutoProcessing = (
  processId: string,
  automatic: boolean
) =>
  axios({
    url: `process/automatic-processing/${processId}`,
    data: { automatic },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Files

export const createGDocModel = (documentTypeId: string) =>
  axios({
    url: "process/document/create",
    method: "POST",
    data: { document_type_id: documentTypeId }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPagedModelList = ({
  page,
  paginator_count = 10,
  search,
  document_type,
  partId,
  capacityId
}: FullFilterPaged) =>
  axios({
    url: `process/document/models/search/`,
    method: "GET",
    params: {
      page,
      paginator_count,
      word: search,
      document_type,
      part: partId,
      capacity: capacityId
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const checkAccessProcess = (processId: string, flow: string) =>
  axios({
    url: `process/check-access/${processId}`,
    method: "GET",
    params: {
      flow
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSignatureHistory = (listProcessId: string[]) =>
  axios({
    url: `process/actions/process-actions`,
    method: "POST",
    data: listProcessId
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const readSignatureRequest = (process_id: string) =>
  axios({
    url: `process/actions/delete-actions/${process_id}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPublicCompleteFile = (processId: string) =>
  axios({ url: `process/public-document/${processId}`, method: "GET" })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getNUP = (nup: string) =>
  axios({
    url: `process/nup/${nup}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessSearch = (nup: string) =>
  axios({
    url: `process/search_nups`,
    method: "GET",
    params: {
      nup
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const returnProcessOrigin = (ReturnProcessList: IProcessReturn[]) =>
  axios({
    url: `process/tramit-to-origin`,
    data: ReturnProcessList,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const validateReturnProcessRequest = (ReturnProcessList: string[]) =>
  axios({
    url: `process/valid-if-process-can-be-returned/`,
    params: {
      process_list_ids: JSON.stringify(ReturnProcessList)
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const returnProcess = (ReturnProcessList: IProcessReturn[]) =>
  axios({
    url: `process/return/`,
    data: ReturnProcessList,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Juntada

export const setJoint = (joint: IDocumentJoin) =>
  axios({
    url: `process/join/edit/${joint.id}`,
    data: joint,
    method: "PUT"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createJoint = (joint: IDocumentJoin) =>
  axios({
    url: `process/join/${joint.id}`,
    data: joint,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteJoint = (jointId: string) =>
  axios({
    url: `process/join/delete/${jointId}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProcessAppended = ({
  processId,
  processAppend
}: {
  processId: string;
  processAppend: IProcessAppend;
}) =>
  axios({
    url: `/process/join/append/${processId}/`,
    data: { ...processAppend },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setProcessUnappended = (
  processId: string,
  { process_join_id, justification }: IProcessAppend
) =>
  axios({
    url: `/process/join/detach/${processId}/`,
    data: { process_join_id, justification },
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getJoint = (processId: string) =>
  axios({
    url: `process/join/draft/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessAppended = (processId: string) =>
  axios({
    url: `/process/join/append/list/${processId}/`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// process tabs count
export const requestProcessTabsCount = (capacities_id: number[]) =>
  axios({
    url: `process/process-tabs-count`,
    method: "POST",
    data: {
      capacity_id: capacities_id
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// get migration details
export const getMigration = (migrationId: string | number) =>
  axios({
    url: "process/get-migration/" + migrationId,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getMigrationsList = ({
  paginator_count,
  page = 1,
  search,
  date,
  order_by
}: FilterPaged) =>
  axios({
    url: `/process/list-migrations/`,
    method: "GET",
    params: { paginator_count, page, search, date, order_by }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const migrateProcess = (data: IProcessMigrate) =>
  axios({
    url: `process/migrate`,
    method: "PATCH",
    data
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const cancelMigrateProcess = (migrationId: string) =>
  axios({
    url: `process/migrate/cancel/${migrationId}`,
    method: "PATCH"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Search
export const getSearchByFilter = ({
  filters,
  page,
  order_by,
  paginator_count = 5
}: FilterPaged) =>
  axios({
    url: `process/search/filters`,
    data: { ...filters, page, order_by, paginator_count },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSearchViproc = (search: string) =>
  axios({
    url: `process/search/viproc`,
    params: { search },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSearchFields = ({
  search_text,
  field
}: {
  search_text: string;
  field: string | null;
}) =>
  axios({
    url: `process/autocomplete/fields`,
    params: { search_text, field },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const reportRequest = ({
  type,
  subject,
  global_bookmarks,
  capacities,
  part,
  start_date,
  end_date,
  file_index
}: IReportRequest) =>
  axios({
    url: `/report/create`,
    method: "POST",
    data: {
      type,
      subject,
      global_bookmarks,
      capacities,
      part,
      start_date,
      end_date,
      file_index
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const forwardProcess = (forwardProcessList: IProcessForward) =>
  axios({
    url: `process/forward`,
    method: "PATCH",
    data: forwardProcessList
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createPendencyRequest = ({
  process_id,
  origin,
  capacity_id,
  pendency_documents,
  observation
}: IProcessPendency) =>
  axios({
    url: `/process/pendency/create`,
    method: "POST",
    data: {
      process_id,
      origin,
      capacity_id,
      documents: pendency_documents,
      observation
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const requestPendingList = (processId: string) =>
  axios({
    url: `/process/pendency/list/${processId}/`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const cancelPendency = (pendencyId: string) =>
  axios({
    url: `/process/pendency/cancel/${pendencyId}/`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPendencyDocumentTypes = (processId: string) =>
  axios({
    url: `/process/pendency/cearadigital/document-types`,
    method: "GET",
    params: {
      process_id: processId
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessFileSize = ({
  processId,
  exclude_draft
}: {
  processId: string;
  exclude_draft: boolean;
}) =>
  axios({
    url: `/process/filesize/${processId}/`,
    method: "GET",
    params: { exclude_draft }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const simpleMyProcessList = ({
  page,
  order_by = JSON.stringify({
    user_responsible_date: { order: "asc", unmapped_type: "date" }
  }),
  paginator_count,
  search
}: FilterPaged) =>
  axios({
    url: `/process/my-process/simplified/`,
    method: "GET",
    params: {
      page,
      order_by,
      paginator_count,
      search
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSimpleNUP = (nup: string) =>
  axios({
    url: `/process/nup/simplified/${nup}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const checkProcessDocumentJoint = (processId: string) =>
  axios({
    url: `/process/valid_if_has_pending_joins/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const reindexProcess = (processId: string) =>
  axios({
    url: `/process/process-reindex/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCanGiveKnowledge = (processId: string) =>
  axios({
    url: `/process/check-can-give-knowledge-process/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessView = (props: {
  processId: string;
  giveKnowledgeId?: string;
}) =>
  axios({
    url: `/process/get-pdf-materialization-tramits/${props.processId}`,
    method: "GET",
    params: { give_knowledge_id: props.giveKnowledgeId }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const downloadProcess = (processId: string) =>
  axios({
    url: `/process/resume/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDownloadProcessStatus = (processId: string) =>
  axios({
    url: `/process/resume/status/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getGroupItemProcess = (params: IProcessGroupRequest) =>
  axios({
    url: `/process/get-tramits-files/`,
    method: "GET",
    params
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCheckResponsible = (ids: string[]) =>
  axios({
    url: `/process/can-try-set-responsible/`,
    method: "GET",
    params: {
      process_id: JSON.stringify(ids)
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCheckDraftDelete = (processId: string) =>
  axios({
    url: `/process/check-draft-delete/${processId}`,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCheckDraftProcessed = (processId: string) =>
  axios({
    url: `/process/check-draft-processed/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProcessSize = (processId: string) =>
  axios({
    url: `/process/process-size/${processId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });
